/**
 * @inpageedit/logger
 *
 * Flexible, Extensible Console Logger with Colored Labels and Hierarchical Loggers
 *
 * @author dragon-fish <dragon-fish@qq.com>
 * @license MIT
 */
var $ = /* @__PURE__ */ ((n) => (n[n.debug = -1] = "debug", n[n.log = 0] = "log", n[n.info = 1] = "info", n[n.warn = 2] = "warn", n[n.error = 3] = "error", n[n.silent = 4] = "silent", n))($ || {});
const f = globalThis || window, d = Symbol.for("__IPE_LOGGER_COLOR_MAP__");
f[d] || (f[d] = /* @__PURE__ */ new Map());
const _ = f[d];
function c(n) {
  let t = 2166136261;
  for (let e = 0; e < n.length; e++)
    t ^= n.charCodeAt(e), t = (t >>> 0) * 16777619;
  return t >>> 0;
}
const g = [
  [350, 360],
  [0, 15],
  [15, 30],
  [200, 230],
  [230, 250],
  [250, 280],
  [280, 310],
  [310, 330],
  [140, 160],
  [160, 180]
];
function E(n, t) {
  const e = g.length, l = c(n) % e, [r, w] = g[l], x = w - r, C = c(n + ":" + t), s = r + C % (x || 1), b = c(n + ":s"), a = c(n + ":l");
  let i, h;
  if (t === "name")
    i = 62 + b % 18, h = 30 + a % 12;
  else {
    const P = s >= 0 && s < 50 || s > 330 && s < 360, R = s >= 200 && s <= 300;
    i = 55 + b % 20, R ? h = 55 + a % 8 : P ? h = 48 + a % 6 : h = 50 + a % 8, i < 60 && (i += 5);
  }
  return `hsl(${s}, ${i}%, ${h}%)`;
}
function u(n, t) {
  if (t)
    return _.set(n, t), t;
  const e = _.get(n);
  if (e) return e;
  const o = n.startsWith("name:") ? "name" : "group", l = E(n, o);
  return _.set(n, l), l;
}
/**
 * @inpageedit/logger
 * Flexible, Extensible Console Logger with Colored Labels and Hierarchical Loggers
 *
 * @author dragon-fish <dragon-fish@qq.com>
 * @license MIT
 *
 * @example
 * ```ts
 * const logger = new Logger({ name: 'MyApp', level: LoggerLevel.debug })
 * // normal usage
 * logger.info('Application started')
 * // create sub-logger group
 * const apiLogger = logger.group('API', { color: '#f59e0b' })
 * apiLogger.warn('Deprecated API endpoint used')
 * // define custom level
 * logger.defineLevel('success', { level: LoggerLevel.info, label: '✅', method: 'info' })
 * logger.success('User created successfully')
 * ```
 */
class m {
  /**
   * Note: constructor returns a callable Proxy so that you can do `logger('group')`.
   */
  constructor(t = {}) {
    this._custom = (e) => {
      if (!this._enabled(e.level)) return v;
      const [o, l] = this._prefix(e.label);
      return this._consoleMethod(e.method).bind(console, o, ...l);
    }, this._name = t.name, this._nameColor = t.color, this._groupPath = t._groupPath ? [...t._groupPath] : [], this._dynamicLevels = { ...t._dynamicLevels }, this._levelRef = t._levelRef ?? {
      value: t.level ?? 1
      /* info */
    }, this._name && u(`name:${this._name}`, this._nameColor), this._installBuiltinLevels();
    for (const e of Object.keys(this._dynamicLevels))
      this._installLevelGetter(e, this._dynamicLevels[e]);
    return y(this);
  }
  // ---------- public API ----------
  get level() {
    return this._levelRef.value;
  }
  set level(t) {
    this._levelRef.value = t;
  }
  /** Create a sub-logger with a group label */
  group(t, e) {
    return t && u(`group:${t}`, e?.color), new m({
      name: this._name,
      color: this._nameColor,
      _groupPath: [...this._groupPath, t],
      _dynamicLevels: this._dynamicLevels,
      _levelRef: this._levelRef
    });
  }
  /** Define a custom level, e.g. logger.defineLevel('success', { level: info, label: '✅', method: 'info' }) */
  defineLevel(t, e) {
    this._dynamicLevels[t] = { ...e }, this._installLevelGetter(t, e);
  }
  // Built-in level getters
  get debug() {
    return this._method("debug");
  }
  get log() {
    return this._method("log");
  }
  get info() {
    return this._method("info");
  }
  get warn() {
    return this._method("warn");
  }
  get error() {
    return this._method("error");
  }
  // ---------- internals ----------
  _installBuiltinLevels() {
  }
  _installLevelGetter(t, e) {
    Object.defineProperty(this, t, {
      configurable: !0,
      enumerable: !1,
      get: () => this._custom(e)
    });
  }
  _method(t) {
    const e = O[t];
    if (!this._enabled(e.level)) return v;
    const [o, l] = this._prefix(e.label);
    return this._consoleMethod(t).bind(console, o, ...l);
  }
  _consoleMethod(t) {
    const e = console[t];
    return (e || console.log).bind(console);
  }
  _enabled(t) {
    return t >= this._levelRef.value && this._levelRef.value !== 4;
  }
  _prefix(t) {
    const e = [];
    let o = "";
    if (this._name) {
      const l = u(`name:${this._name}`, this._nameColor);
      o += `%c${this._name}%c`, e.push(
        `background:${l}; color:#fff; padding:1px 4px; border-radius:2px; font-weight:700;`,
        p
      );
    }
    if (t && (o += ` ${t}`), this._groupPath.length) {
      const l = this._groupPath.join("/"), r = u(`group:${l}`);
      o += ` %c${l}%c`, e.push(`color:${r}; text-decoration: underline;`, p);
    }
    return [o, e];
  }
}
const v = () => {
}, p = "color:inherit; background:transparent; text-decoration:none;", O = {
  debug: { level: -1, label: "", method: "debug" },
  log: { level: 0, label: "", method: "log" },
  info: { level: 1, label: "[I]", method: "info" },
  warn: { level: 2, label: "[W]", method: "warn" },
  error: { level: 3, label: "[E]", method: "error" }
};
function y(n) {
  const t = function(e, o) {
    return n.group(e, o);
  };
  return new Proxy(t, {
    get(e, o, l) {
      return n[o];
    },
    set(e, o, l) {
      return n[o] = l, !0;
    },
    apply(e, o, l) {
      return n.group(l[0], l[1]);
    },
    has(e, o) {
      return o in n;
    }
  });
}
function S(n) {
  return new m(n);
}
export {
  m as Logger,
  $ as LoggerLevel,
  S as createLogger
};
//# sourceMappingURL=index.js.map
